import { ChangeDetectionStrategy, Component, inject, Input, signal, WritableSignal } from '@angular/core';
import { GamePlayerModel, MODAL_TOKEN, ModalProvider } from '@core';
import { OrAvatarComponent } from '@ui-components';
import { IonicModule } from '@ionic/angular';
import { cardReasonMapper } from '../../application/utils';
import { CardReasonModel } from '../../application/interfaces';
import { take } from 'rxjs';
import { AddCardCommandHandler, AddCardCommandPayload } from '../../application/handlers';
import { PenaltiesService } from '@fair-play/infra';
import { AddCardSuccessModalComponent } from './add-card-success-modal/add-card-success-modal.component';
import { PenaltyReason } from '@core-http';

@Component({
  selector: 'lib-add-card-modal',
  templateUrl: './add-card-modal.component.html',
  styleUrls: ['./add-card-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [OrAvatarComponent, IonicModule],
  providers: [AddCardCommandHandler, PenaltiesService]
})
export class AddCardModalComponent {
  @Input() gameId!: string;
  @Input() player!: GamePlayerModel;

  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly addCardCommandHandler: AddCardCommandHandler = inject(AddCardCommandHandler);

  readonly reasons: CardReasonModel[] = Object.values(cardReasonMapper);
  readonly selectedReason: WritableSignal<PenaltyReason | null> = signal<PenaltyReason | null>(null);

  closeModal(): void {
    this.modalProvider.dismissModal$();
  }

  addCard(): void {
    const reason: PenaltyReason | null = this.selectedReason();
    if (!reason) return;

    const payload: AddCardCommandPayload = {
      gameId: this.gameId,
      userId: this.player.userId!,
      reason: reason,
      penaltyType: cardReasonMapper[reason].cardType
    };

    this.addCardCommandHandler
      .add(payload)
      .pipe(take(1))
      .subscribe(() => {
        this.modalProvider.dismissModal$();
        this.confirmationModal(payload.reason);
      });
  }

  select(event: CustomEvent): void {
    this.selectedReason.set(event.detail.value);
  }

  selectBox(value: PenaltyReason): void {
    this.selectedReason.set(value);
  }

  isSelected(value: PenaltyReason): boolean {
    return this.selectedReason() === value;
  }

  private confirmationModal(reason: PenaltyReason): void {
    this.modalProvider
      .showModal$({
        component: AddCardSuccessModalComponent,
        cssClass: 'present-modal',
        componentProps: {
          reason: reason,
          player: this.player
        }
      })
      .subscribe(() => this.modalProvider.dismissAllStoredModals());
  }
}
